import React from 'react';
import { Moon, Battery, Clock, CloudMoon, Bed, BarChart3, Info, Pill, AlertTriangle } from 'lucide-react';
import SaveToIdeasButton from '../components/SaveToIdeasButton';

const SleepRecoveryPage = () => {
  return (
    <div className="space-y-8 max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-4">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-indigo-100 rounded-xl">
              <Moon className="h-8 w-8 text-indigo-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-indigo-600 mb-1">Integrative Health</p>
              <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-slate-900">
                Sleep & Recovery
              </h1>
            </div>
          </div>
          <SaveToIdeasButton
            title="Sleep & Recovery"
            category="Sleep"
            sourcePage="Sleep & Recovery"
            sourceUrl="/sleep-recovery"
            variant="compact"
          />
        </div>
      </div>

      {/* Main Content Card */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        
        {/* Section 1: Why sleep is important */}
        <div className="p-6 md:p-8 border-b border-slate-100">
          <h2 className="text-xl md:text-2xl font-semibold text-slate-900 mb-4">
            Why sleep is important for overall health
          </h2>
          <div className="space-y-4 text-slate-700 leading-relaxed">
            <p>
              Sleep is one of the most important foundations of health. During sleep, the body carries out essential repair and recovery processes that support the brain, immune system, muscles, and internal organs.
            </p>
            <div className="bg-indigo-50 border border-indigo-100 rounded-xl p-4">
              <p className="font-semibold text-indigo-900">
                Good sleep is not a luxury. It is a biological necessity.
              </p>
            </div>
          </div>
        </div>

        {/* Section 2: Sleep and the body's recovery systems */}
        <div className="p-6 md:p-8 border-b border-slate-100 bg-slate-50/50">
          <div className="flex items-start gap-4">
            <div className="p-2 bg-teal-100 rounded-lg flex-shrink-0 mt-1">
              <Battery className="h-5 w-5 text-teal-600" />
            </div>
            <div>
              <h2 className="text-xl md:text-2xl font-semibold text-slate-900 mb-4">
                Sleep and the body's recovery systems
              </h2>
              <div className="space-y-4 text-slate-700 leading-relaxed">
                <p className="mb-3">While you sleep, your body:</p>
                <ul className="space-y-2 ml-1">
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-teal-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span>Repairs tissues</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-teal-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span>Regulates hormones</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-teal-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span>Supports immune function</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-teal-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span>Processes stress</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-teal-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span>Restores energy levels</span>
                  </li>
                </ul>
                <p className="mt-4 text-slate-600 italic">
                  Poor or irregular sleep can make fatigue worse and reduce the body's ability to recover from daily stress.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Section 3: How much sleep is recommended */}
        <div className="p-6 md:p-8 border-b border-slate-100">
          <div className="flex items-start gap-4">
            <div className="p-2 bg-blue-100 rounded-lg flex-shrink-0 mt-1">
              <Clock className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-xl md:text-2xl font-semibold text-slate-900 mb-4">
                How much sleep is generally recommended
              </h2>
              <div className="space-y-4 text-slate-700 leading-relaxed">
                <p className="mb-3">Sleep needs vary between individuals, but many adults function best with:</p>
                <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 inline-block">
                  <p className="font-semibold text-blue-900 text-lg">
                    Approximately 7–9 hours of sleep per night
                  </p>
                </div>
                <p className="mt-4 font-medium text-slate-800">
                  Quality and consistency are just as important as duration.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Section 4: Why sleep can be disrupted in CLL */}
        <div className="p-6 md:p-8 border-b border-slate-100 bg-amber-50/50">
          <div className="flex items-start gap-4">
            <div className="p-2 bg-amber-100 rounded-lg flex-shrink-0 mt-1">
              <CloudMoon className="h-5 w-5 text-amber-600" />
            </div>
            <div>
              <h2 className="text-xl md:text-2xl font-semibold text-slate-900 mb-4">
                Why sleep can be disrupted in CLL
              </h2>
              <div className="space-y-4 text-slate-700 leading-relaxed">
                <p className="mb-3">People living with CLL may experience sleep disruption due to:</p>
                <ul className="space-y-2 ml-1">
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-amber-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span>Stress or anxiety</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-amber-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span>Fatigue</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-amber-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span>Night-time worry</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-amber-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span>Irregular routines</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-amber-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span>Physical discomfort</span>
                  </li>
                </ul>
                <div className="mt-4 bg-white border border-amber-200 rounded-xl p-4">
                  <p className="font-medium text-slate-800">
                    Sleep problems do not mean something is "wrong" — they are common and manageable.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Section 5: Gentle sleep-supporting routines */}
        <div className="p-6 md:p-8 border-b border-slate-100">
          <div className="flex items-start gap-4">
            <div className="p-2 bg-purple-100 rounded-lg flex-shrink-0 mt-1">
              <Bed className="h-5 w-5 text-purple-600" />
            </div>
            <div>
              <h2 className="text-xl md:text-2xl font-semibold text-slate-900 mb-4">
                Gentle sleep-supporting routines
              </h2>
              <div className="space-y-4 text-slate-700 leading-relaxed">
                <p className="mb-3">Helpful sleep habits may include:</p>
                <ul className="space-y-2 ml-1">
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-purple-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span>Going to bed and waking up at similar times each day</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-purple-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span>Reducing screen use before bed</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-purple-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span>Creating a calm, dark sleep environment</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-purple-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span>Avoiding heavy meals close to bedtime</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-purple-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span>Establishing a wind-down routine</span>
                  </li>
                </ul>
                <p className="mt-4 text-slate-600 italic">
                  Not every suggestion works for everyone. Small improvements can still make a meaningful difference.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Section 6: Melatonin and Sleep (Educational) */}
        <div className="p-6 md:p-8 border-b border-slate-100 bg-indigo-50/50">
          <div className="flex items-start gap-4">
            <div className="p-2 bg-indigo-100 rounded-lg flex-shrink-0 mt-1">
              <Pill className="h-5 w-5 text-indigo-600" />
            </div>
            <div>
              <h2 className="text-xl md:text-2xl font-semibold text-slate-900 mb-4">
                Melatonin and Sleep (Educational)
              </h2>
              <div className="space-y-4 text-slate-700 leading-relaxed">
                <p>
                  Melatonin is a hormone naturally produced by the body that helps regulate sleep–wake cycles. It is often referred to as the "sleep hormone" because levels typically rise in the evening as the body prepares for rest.
                </p>
                <p>
                  Some people with CLL report sleep disruption, and melatonin is commonly discussed as a potential support for improving sleep quality.
                </p>
                
                <h3 className="text-lg font-medium text-slate-800 mt-6 mb-3">Why melatonin is discussed</h3>
                <ul className="space-y-2 ml-1">
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-indigo-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span>It may help with falling asleep</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-indigo-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span>It is available over the counter in many countries</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-indigo-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span>It is generally considered well-tolerated at low doses</span>
                  </li>
                </ul>

                <div className="mt-4 bg-white border border-indigo-200 rounded-xl p-4">
                  <p className="font-medium text-slate-800">
                    Melatonin is not a treatment for CLL or any medical condition. Its effects on sleep vary between individuals, and dosage and timing matter significantly.
                  </p>
                </div>

                <h3 className="text-lg font-medium text-slate-800 mt-6 mb-3">Safety considerations</h3>
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-amber-800 mb-2">Important to be aware of:</p>
                      <ul className="space-y-1 text-amber-900 text-sm">
                        <li>• Daytime drowsiness may occur</li>
                        <li>• Some people experience vivid dreams</li>
                        <li>• Melatonin may interact with certain medications</li>
                        <li>• Long-term effects are not fully understood</li>
                        <li>• Quality varies between products</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <p className="mt-4">
                  If you are considering melatonin, it is important to discuss this with your healthcare team, especially if you are taking other medications or undergoing treatment.
                </p>

                {/* Key message callout */}
                <div className="mt-6 bg-indigo-50 border border-indigo-200 rounded-xl p-4">
                  <p className="text-indigo-900 font-medium text-center">
                    Good sleep supports overall wellbeing. Small supports can help, but guidance matters.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Section 7: Tracking sleep and wellbeing */}
        <div className="p-6 md:p-8 bg-green-50/70">
          <div className="flex items-start gap-4">
            <div className="p-2 bg-green-100 rounded-lg flex-shrink-0 mt-1">
              <BarChart3 className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <h2 className="text-xl md:text-2xl font-semibold text-slate-900 mb-4">
                Tracking sleep and wellbeing
              </h2>
              <div className="space-y-4 text-slate-700 leading-relaxed">
                <p className="mb-3">This platform allows you to reflect on sleep alongside:</p>
                <ul className="space-y-2 ml-1">
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span>Energy levels</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span>Mood</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span>Stress</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span>Blood results over time</span>
                  </li>
                </ul>
                <p className="mt-4 font-medium text-slate-800">
                  Observing patterns can help you better understand what supports your wellbeing.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Educational Note Footer */}
      <div className="bg-slate-100 rounded-xl p-5 border border-slate-200">
        <div className="flex items-start gap-3">
          <Info className="h-5 w-5 text-slate-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-slate-700 mb-1">Educational note</p>
            <p className="text-sm text-slate-600 leading-relaxed">
              This information is provided for general education only and does not replace medical advice. Persistent sleep problems should be discussed with a qualified healthcare professional.
            </p>
          </div>
        </div>
      </div>

      {/* Bottom Save Button */}
      <div className="flex justify-center pt-4">
        <SaveToIdeasButton
          title="Sleep & Recovery"
          category="Sleep"
          sourcePage="Sleep & Recovery"
          sourceUrl="/sleep-recovery"
        />
      </div>
    </div>
  );
};

export default SleepRecoveryPage;
